// @owners { team: patients }
import { type Storage } from 'redux-persist';
import { Storage as MMKVStorage } from './storage';
import { isQuotaExceededError } from './storageQuotaErrors';

function rejectWithError(error: unknown): Promise<never> {
  return Promise.reject(error instanceof Error ? error : new Error(String(error)));
}

/**
 * redux-persist does `storage.setItem(...).catch(onWriteFail)`. If setItem throws
 * synchronously (MMKV does), the failure is unhandled. Every method here must
 * return a Promise and must not throw synchronously.
 */
function trySetAfterClearingKey(key: string, value: string): void {
  try {
    MMKVStorage.remove(key);
  } catch {
    // ignore — best-effort free space for retry
  }
  MMKVStorage.set(key, value);
}

export const reduxStorage: Storage = {
  // MMKV is synchronous but returning a promise for compatibility with redux-persist which expects async methods
  getItem: (key: string): Promise<string | null> => {
    try {
      return Promise.resolve(MMKVStorage.getString(key) ?? null);
    } catch (error) {
      if (isQuotaExceededError(error)) {
        return Promise.resolve(null);
      }
      return rejectWithError(error);
    }
  },

  // MMKV is synchronous but returning a promise for compatibility with redux-persist which expects async methods
  setItem: (key: string, value: string): Promise<void> => {
    try {
      MMKVStorage.set(key, value);
      return Promise.resolve();
    } catch (error) {
      if (!isQuotaExceededError(error)) {
        return rejectWithError(error);
      }
      try {
        trySetAfterClearingKey(key, value);
        return Promise.resolve();
      } catch (retryError) {
        if (!isQuotaExceededError(retryError)) {
          return rejectWithError(retryError);
        }
        // Resolve so redux-persist does not treat this as a rejected write loop;
        // in-memory Redux state remains authoritative until a later persist succeeds.
        return Promise.resolve();
      }
    }
  },

  // MMKV is synchronous but returning a promise for compatibility with redux-persist which expects async methods
  removeItem: (key: string): Promise<void> => {
    try {
      MMKVStorage.remove(key);
      return Promise.resolve();
    } catch (error) {
      if (isQuotaExceededError(error)) {
        return Promise.resolve();
      }
      return rejectWithError(error);
    }
  },
};
